import * as React from "react";
import { DropdownMenu as D } from "radix-ui";
import { Check, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export const DropdownMenu = D.Root;
export const DropdownMenuTrigger = D.Trigger;
export const DropdownMenuGroup = D.Group;
export const DropdownMenuSub = D.Sub;
export const DropdownMenuRadioGroup = D.RadioGroup;

const contentClass =
  "z-50 min-w-[200px] overflow-hidden rounded-lg border border-border bg-popover p-1 text-popover-foreground shadow-pop outline-none data-[state=open]:animate-pop-in data-[state=closed]:animate-pop-out";
const itemClass =
  "relative flex h-8 cursor-default select-none items-center gap-2 rounded-md px-2 text-[13.5px] outline-none data-[disabled]:pointer-events-none data-[disabled]:opacity-45 data-[highlighted]:bg-hover [&_svg]:size-4 [&_svg]:shrink-0 [&_svg:not([data-tone])]:text-muted-foreground";

export const DropdownMenuContent = React.forwardRef<
  React.ComponentRef<typeof D.Content>,
  React.ComponentPropsWithoutRef<typeof D.Content>
>(function DropdownMenuContent({ className, sideOffset = 6, align = "start", ...props }, ref) {
  return (
    <D.Portal>
      <D.Content
        ref={ref}
        sideOffset={sideOffset}
        align={align}
        collisionPadding={8}
        className={cn(contentClass, className)}
        {...props}
      />
    </D.Portal>
  );
});

export const DropdownMenuItem = React.forwardRef<
  React.ComponentRef<typeof D.Item>,
  React.ComponentPropsWithoutRef<typeof D.Item> & { destructive?: boolean; shortcut?: string }
>(function DropdownMenuItem({ className, destructive, shortcut, children, ...props }, ref) {
  return (
    <D.Item
      ref={ref}
      className={cn(itemClass, destructive && "text-destructive [&_svg]:text-destructive", className)}
      {...props}
    >
      {children}
      {shortcut && <span className="ml-auto pl-4 text-xs text-subtle">{shortcut}</span>}
    </D.Item>
  );
});

export const DropdownMenuCheckboxItem = React.forwardRef<
  React.ComponentRef<typeof D.CheckboxItem>,
  React.ComponentPropsWithoutRef<typeof D.CheckboxItem>
>(function DropdownMenuCheckboxItem({ className, children, ...props }, ref) {
  return (
    <D.CheckboxItem ref={ref} className={cn(itemClass, "pr-8", className)} {...props}>
      {children}
      <D.ItemIndicator className="absolute right-2 inline-flex">
        <Check className="!text-foreground" />
      </D.ItemIndicator>
    </D.CheckboxItem>
  );
});

export const DropdownMenuRadioItem = React.forwardRef<
  React.ComponentRef<typeof D.RadioItem>,
  React.ComponentPropsWithoutRef<typeof D.RadioItem> & { shortcut?: string }
>(function DropdownMenuRadioItem({ className, children, shortcut, ...props }, ref) {
  return (
    <D.RadioItem ref={ref} className={cn(itemClass, "pr-8", className)} {...props}>
      {children}
      {shortcut && <span className="ml-auto pl-4 text-xs text-subtle">{shortcut}</span>}
      <D.ItemIndicator className="absolute right-2 inline-flex">
        <Check className="!text-foreground" />
      </D.ItemIndicator>
    </D.RadioItem>
  );
});

export function DropdownMenuLabel({ className, ...props }: React.ComponentPropsWithoutRef<typeof D.Label>) {
  return <D.Label className={cn("px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground", className)} {...props} />;
}

export function DropdownMenuSeparator({ className, ...props }: React.ComponentPropsWithoutRef<typeof D.Separator>) {
  return <D.Separator className={cn("-mx-1 my-1 h-px bg-border", className)} {...props} />;
}

export function DropdownMenuSubTrigger({ className, children, ...props }: React.ComponentPropsWithoutRef<typeof D.SubTrigger>) {
  return (
    <D.SubTrigger className={cn(itemClass, "data-[state=open]:bg-hover", className)} {...props}>
      {children}
      <ChevronRight className="ml-auto" />
    </D.SubTrigger>
  );
}

export function DropdownMenuSubContent({ className, ...props }: React.ComponentPropsWithoutRef<typeof D.SubContent>) {
  return (
    <D.Portal>
      <D.SubContent sideOffset={4} collisionPadding={8} className={cn(contentClass, className)} {...props} />
    </D.Portal>
  );
}
